import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { HandleTokenService } from '../../../core/services/handle-token.service';
import { AppFacade } from '../../../facades/app.facade';
import { ErrorFacade } from '../../../facades/error.facade';
import { MapErrorService } from '../../services/error/map-error.service';

import { Store } from '@ngrx/store';
import {
  catchError,
  delay,
  map,
  concatMap,
  switchMap,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { APP_ROUTES as ROUTES } from '../../../app.routes';
import { UtilService } from '../../../services/util.service';

import { APP_CONSTANTS as CONST } from '@app/app.constants';
import { environment as ENV } from '@environment';
import * as appSelectors from '@store/app/app.selectors';
import * as customerActions from '@store/customer/customer.actions';
import * as customerSelectors from '@store/customer/customer.selectors';
import * as biometricsSelectors from '@store/biometrics/biometrics.selectors';
import * as paymentSelectors from '@store/payments/payments.selectors';
import { interval, of } from 'rxjs';
import {
  EVALIDATOR_COMPLETED,
  EVALIDATION_STATUS,
  EVALIDATION_STATUS as STATUS,
} from '../../../core/enums/customer.enum';
import { IExtraData, ILargeContentConfig, IPutRecord } from '../../../core/models/app.model';
import {
  ICustomerCard,
  ICustomerValidationResponse,
  IPreLoan,
  IUnifiedScore,
} from '../../../core/models/customer.model';
import {
  IPaymentParams,
  IRequestPaymentParams,
  IRequestPaymentsConfig,
} from '../../../core/models/payments.model';
import { CustomerAPI } from '../../../data/mo-api/customer.api';
import { CustomerFacade } from '../../../facades/customer.facade';
import { PaymentsFacade } from '../../../facades/payments.facade';
import { UIService } from '../../../services/ui.service';
import { Customer } from '../../classes/customer.class';
import { EVENTS } from '../../../data/events/events-by-category';
import { Title } from '@angular/platform-browser';
import { ERROR_COMPONENT_CONSTANTS as ERROR } from '@app/domain/services/error/error.constants';
import { EERROR_PAGE_TYPES, IErrorComponentData } from '@core/models/error.model';
import { AppAPI } from '@app/data/mo-api/app.api';
import { PaymentsAPI } from '@app/data/mo-api/payments.api';
import { EFAST_ONBOARDING_STEPS, EONBOARDING_STEPS } from '@app/core/enums/onboarding.enum';
import { OffersFacade } from '@app/facades/offers.facade';
import { OffersAPI } from '@app/data/mo-api/offers.api';
import { OnboardingFacade } from '@app/facades/onboarding.facade';
import { ValidationsService } from '@app/domain/services/validations.service';
import { ErrorService } from '@core/services/error.service';

@Injectable()
export class CustomerEffects {
  constructor(
    private actions$: Actions,
    private _store: Store,
    private _handleTokenService: HandleTokenService,
    private _utilService: UtilService,
    private _mapErrorService: MapErrorService,
    private _uiService: UIService,
    private _validationsService: ValidationsService,
    private _titleService: Title,
    private _customerFacade: CustomerFacade,
    private _errorFacade: ErrorFacade,
    private _errorService: ErrorService,
    private _appFacade: AppFacade,
    private _paymentFacade: PaymentsFacade,
    private _offersFacade: OffersFacade,
    private _onboardingFacade: OnboardingFacade,
    private _appAPI: AppAPI,
    private _customerAPI: CustomerAPI,
    private _paymentsAPI: PaymentsAPI,
    private _offersAPI: OffersAPI
  ) {}

  public requestCustomerInformation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerActions.requestCustomerInformation),
      switchMap((_) =>
        this._customerAPI.requestCustomerInformation().pipe(
          map((customer) => {
            return customerActions.successRequestCustomerInformation({
              customer: new Customer(customer),
            });
          }),
          catchError((error) => of(customerActions.failureRequestCustomerInformation({ error })))
        )
      )
    )
  );

  public successRequestCustomerInformation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.successRequestCustomerInformation),
        withLatestFrom(this._store.select(appSelectors.selectStep)),
        tap(([_, step]) => {
          this._handleTokenService.setUserStep(step);
        })
      ),
    { dispatch: false }
  );

  public failureRequestCustomerInformation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.failureRequestCustomerInformation),
        tap(({ error }) => {
          const message = this._mapErrorService.mapError(error);
          this._errorFacade.handleError(message);
        })
      ),
    { dispatch: false }
  );

  public listenCustomerValidation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.listenCustomerValidation),
        withLatestFrom(
          this._store.select(appSelectors.selectTransactions),
          this._store.select(appSelectors.selectFastOnboardingFlag),
          this._store.select(biometricsSelectors.selectIsBiometricsFlag),
          this._store.select(appSelectors.selectPurchaseLessOnboardingFlag),
          this._store.select(appSelectors.selectSessionId)
        ),
        tap(
          ([_, transactions, fastOnboarding, isBiometrics, purchaselessOnboarding, sessionId]) => {
            return interval(CONST.VALIDATION_REQUEST_LOOP)
              .pipe(
                concatMap(() =>
                  this._customerAPI.requestCustomerValidation(transactions?.id, sessionId)
                ),
                tap((validation: ICustomerValidationResponse) => {
                  if (validation.validation_status === STATUS.PENDING) return false;
                  const data: IPutRecord = EVENTS.KYC.REQUEST;
                  const extraData: IExtraData = {
                    request: { transaction_id: transactions?.id },
                    response: validation,
                  };
                  this._appFacade.sendPutRecord(data, extraData);
                  return this._customerFacade.successListenCustomerValidation(validation);
                }),
                takeWhile(({ validation_status, validator_completed }) =>
                  !!isBiometrics
                    ? validation_status === EVALIDATION_STATUS.PENDING
                    : validation_status === EVALIDATION_STATUS.PENDING &&
                      validator_completed != EVALIDATOR_COMPLETED.BIOMETRICS
                ),
                catchError((error) => {
                  const data: IPutRecord = EVENTS.KYC.REQUEST;
                  const extraData: IExtraData = {
                    request: { transaction_id: transactions?.id },
                    response: { error: error?.error ?? error },
                  };
                  this._appFacade.sendPutRecord(data, extraData);
                  return of(this._appFacade.killPurchase());
                })
              )
              .subscribe();
          }
        )
      ),
    { dispatch: false }
  );

  public successListenCustomerValidation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.successListenCustomerValidation),
        withLatestFrom(
          this._store.select(appSelectors.selectSession),
          this._store.select(appSelectors.selectFastOnboardingFlag),
          this._store.select(appSelectors.selectStep),
          this._store.select(biometricsSelectors.selectIsBiometricsFlag),
          this._store.select(appSelectors.selectPurchaseLessOnboardingFlag)
        ),
        tap(
          ([
            { validation },
            session,
            fastOnboarding,
            appStep,
            isBiometrics,
            isPurchaselessOnboarding,
          ]) => {
            const { token, is_customer } = session;

            let error: boolean,
              componentData: IErrorComponentData,
              route: string,
              step: number,
              re_validation: boolean;

            //Retry listen customer validation
            if (
              validation?.can_request_validator &&
              validation?.validation_status === EVALIDATION_STATUS.ERROR
            ) {
              return this._store.dispatch(customerActions.listenCustomerValidation());
            }

            //Biometrics validation
            if (
              validation?.validator_completed === EVALIDATOR_COMPLETED.BIOMETRICS &&
              validation?.validation_status === EVALIDATION_STATUS.PENDING &&
              !isBiometrics
            ) {
              return this._utilService.navigateTo(ROUTES.BIOMETRICS);
            }

            // Blocking errors validation
            ({ error, componentData, route, step, re_validation } =
              this._validationsService.customerValidationBlockingErrors(validation));

            if (!!error && !!componentData) {
              if (!isPurchaselessOnboarding) {
                return this._appFacade.killPurchaseWithoutRedirect(
                  'blocking-rule',
                  is_customer,
                  componentData
                );
              }
              return this._errorFacade.showErrorComponent(
                this._errorService.mapErrorButton(componentData, isPurchaselessOnboarding)
              );
            }

            // Retry errors validation
            ({ error, componentData, route, step, re_validation } =
              this._validationsService.customerValidationRetryErrors(validation));

            if (!error && !!componentData) {
              return this._errorFacade.showErrorComponent(
                this._errorService.mapErrorButton(
                  componentData,
                  isPurchaselessOnboarding,
                  fastOnboarding
                )
              );
            }

            if (!!fastOnboarding) {
              route = ROUTES.FAST_ONBOARDING_STEP_2;
              this._onboardingFacade.setNewOnboardingStep(EFAST_ONBOARDING_STEPS.PERSONAL_FORM);
              this._utilService.navigateTo(route);
              return;
            } else if (!isPurchaselessOnboarding) {
              return this._customerFacade.requestAggregatorValidation();
            } else {
              return this._customerFacade.getCustomerCard();
            }
          }
        )
      ),
    { dispatch: false }
  );

  public requestAggregatorValidation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerActions.requestAggregatorValidation),
      withLatestFrom(
        this._store.select(appSelectors.selectTransactions),
        this._store.select(appSelectors.selectSessionId)
      ),
      switchMap(([_, transaction, sessionId]) => {
        return this._customerAPI.requestAggregatorValidation(transaction?.id, sessionId).pipe(
          map((aggregatorValidation) =>
            customerActions.successRequestAggregatorValidation({ aggregatorValidation })
          ),
          catchError((error) => of(customerActions.failureRequestAggregatorValidation({ error })))
        );
      })
    )
  );

  public successRequestAggregatorValidation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.successRequestAggregatorValidation),
        withLatestFrom(this._store.select(appSelectors.selectSession)),
        tap(([{ aggregatorValidation }, session]) => {
          if (aggregatorValidation?.can_request_loan) {
            return this._customerFacade.requestMaxInstallmentValidation();
          }

          const { error, componentData } =
            this._validationsService.handleAggregatorValidations(aggregatorValidation);

          if (!!error && !!componentData) {
            return this._appFacade.killPurchaseWithoutRedirect(
              'blocking-rule',
              session?.is_customer,
              componentData
            );
          }
        })
      ),
    { dispatch: false }
  );

  public failureRequestAggregatorValidation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.failureRequestAggregatorValidation),
        withLatestFrom(this._store.select(appSelectors.selectSession)),
        tap(([_, session]) => {
          const errorContent = ERROR[EERROR_PAGE_TYPES.ERROR_01];
          this._appFacade.killPurchaseWithoutRedirect(
            'error-default',
            session?.is_customer,
            errorContent
          );
        })
      ),
    { dispatch: false }
  );

  public requestMaxInstallmentValidation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerActions.requestMaxInstallmentValidation),
      withLatestFrom(
        this._store.select(appSelectors.selectTransactions),
        this._store.select(appSelectors.selectSessionId)
      ),
      switchMap(([_, transaction, sessionId]) => {
        return this._customerAPI.requestMaxInstallmentValidation(transaction?.id, sessionId).pipe(
          map((maxInstallmentValidation) =>
            customerActions.successRequestMaxInstallmentValidation({ maxInstallmentValidation })
          ),
          catchError((error) =>
            of(customerActions.failureRequestMaxInstallmentValidation({ error }))
          )
        );
      })
    )
  );

  public successRequestMaxInstallmentValidation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.successRequestMaxInstallmentValidation),
        withLatestFrom(this._store.select(appSelectors.selectSession)),
        tap(([{ maxInstallmentValidation }, session]) => {
          let error: boolean,
            componentData: IErrorComponentData,
            route: string,
            step: number,
            re_validation: boolean;

          ({ error, componentData, route } =
            this._validationsService.handleMaxInstallmentValidations(maxInstallmentValidation));

          // CASE 1 MAX INSTALLMENT - HAPPY PATH
          if (maxInstallmentValidation?.can_have_max_installment) {
            return this._customerFacade.requestDebitDecisionValidation();
            // route = ROUTES.PAYMENT_PLAN;
            // return this._customerFacade.checkPendingPayments(route);
          }

          //CASE 4 MAX INSTALLMENT - ALL FLAGS IN FALSE
          if (!!error && !!componentData) {
            return this._appFacade.killPurchaseWithoutRedirect(
              'blocking-rule',
              session?.is_customer,
              componentData
            );
          }

          if (route === ROUTES.WAITING_ROOM) {
            // CASE 2 MAX INSTALLMENT - REDIRECT TO OFFERS
            return this._customerFacade.checkPendingPayments(route);
          }

          if (route === ROUTES.NO_AVAILABLE_OFFER) {
            // CASE 3 MAX INSTALLMENT - CONSULT SCORE AND SHOW 2 ERRORS
            this._customerFacade.requestAvailableScore(route);
          }
        })
      ),
    { dispatch: false }
  );

  public failureRequestMaxInstallmentValidation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.failureRequestMaxInstallmentValidation),
        withLatestFrom(this._store.select(appSelectors.selectSession)),
        tap(([_, session]) => {
          const componentData = ERROR[EERROR_PAGE_TYPES.ERROR_01];
          this._appFacade.killPurchaseWithoutRedirect(
            'error-default',
            session?.is_customer,
            componentData
          );
        })
      ),
    { dispatch: false }
  );

  public requestDebitDecisionValidation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerActions.requestDebitDecisionValidation),
      withLatestFrom(
        this._store.select(appSelectors.selectTransactions),
        this._store.select(appSelectors.selectSessionId)
      ),
      switchMap(([_, transaction, sessionId]) => {
        return this._customerAPI.requestDebitValidation(transaction?.id, sessionId).pipe(
          map((debitDecisionValidation) =>
            customerActions.successRequestDebitDecisionValidation({ debitDecisionValidation })
          ),
          catchError((error) =>
            of(customerActions.failureRequestDebitDecisionValidation({ error }))
          )
        );
      })
    )
  );

  public successRequestDebitDecisionValidation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.successRequestDebitDecisionValidation),
        tap((_) => {
          const route: string = ROUTES.PAYMENT_PLAN;
          this._customerFacade.checkPendingPayments(route);
        })
      ),
    { dispatch: false }
  );

  public failureRequestDebitDecisionValidation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.failureRequestDebitDecisionValidation),
        withLatestFrom(this._store.select(appSelectors.selectSession)),
        tap(([_, session]) => {
          const componentData = ERROR[EERROR_PAGE_TYPES.ERROR_01];
          this._appFacade.killPurchaseWithoutRedirect(
            'error-default',
            session?.is_customer,
            componentData
          );
        })
      ),
    { dispatch: false }
  );

  public checkPendingPayments$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.checkPendingPayments),
        withLatestFrom(
          this._store.select(appSelectors.selectTransactions),
          this._store.select(appSelectors.selectValidation)
        ),
        switchMap(([{ route }, transaction, { has_card }]) => {
          const config: IRequestPaymentsConfig = {
            limit: 1,
            status: 'pending',
          };
          return this._paymentsAPI.checkPendingPayments(transaction?.id, config).pipe(
            map((requestPayments) => {
              if (has_card) this._customerFacade.getCustomerCard();
              if (requestPayments.results.length > 0) {
                const payment = requestPayments.results[0];
                const requestPaymentParams: IRequestPaymentParams = {
                  payment_id: payment.payment_id,
                  transaction_id: transaction?.id,
                  was_redirected: false,
                };
                this._appFacade.setNewStep(EONBOARDING_STEPS.PAYMENT_METHOD);
                return this._paymentFacade.successCreatePayments(requestPaymentParams);
              } else if (route === ROUTES.WAITING_ROOM) {
                return this._offersFacade.getAvailableOffers(transaction?.id);
              } else {
                if (route === ROUTES.PAYMENT_PLAN) {
                  this._appFacade.setNewStep(EONBOARDING_STEPS.PAYMENT_PLAN);
                }
                this._utilService.navigateTo(route);
              }
            })
          );
        })
      ),
    { dispatch: false }
  );

  public requestAvailableScore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerActions.requestAvailableScore),
      withLatestFrom(
        this._store.select(appSelectors.selectTransactions),
        this._store.select(customerSelectors.selectCustomerValidation),
        this._store.select(appSelectors.selectSessionId)
      ),
      switchMap(([{ route }, transaction, { has_card }, sessionId]) => {
        if (!has_card)
          return this._customerAPI.requestAvailableScore(transaction?.id, sessionId).pipe(
            map((score) => customerActions.successRequestAvailableScore({ score, route })),
            catchError((error) => of(customerActions.failureRequestAvailableScore({ error })))
          );

        return this._offersAPI.getAvailableOffers(transaction?.id).pipe(
          map((availableOffers) => {
            const unifiedScore: IUnifiedScore = {
              score: availableOffers?.score,
              offers: availableOffers?.offers,
            };
            return customerActions.successRequestAvailableScore({ score: unifiedScore, route });
          }),
          catchError((error) => of(customerActions.failureRequestAvailableScore({ error })))
        );
      })
    )
  );

  public successRequestAvailableScore$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.successRequestAvailableScore),
        tap(({ route }) => {
          this._utilService.navigateTo(route);
        })
      ),
    { dispatch: false }
  );

  public failureRequestAvailableScore$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.failureRequestAvailableScore),
        withLatestFrom(this._store.select(appSelectors.selectSession)),
        tap(([_, { is_customer }]) => {
          const errorContent = ERROR[EERROR_PAGE_TYPES.ERROR_01];
          this._appFacade.killPurchaseWithoutRedirect(
            'error-request-available-score',
            is_customer,
            errorContent
          );
        })
      ),
    { dispatch: false }
  );

  public redirectUserToScore$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.redirectUserToScore),
        delay(10000),
        tap((_) => {
          const route: string = ROUTES.SCORE;
          this._utilService.navigateTo(route);
        })
      ),
    { dispatch: false }
  );

  public requestPreLoan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerActions.requestPreLoan),
      withLatestFrom(this._store.select(appSelectors.selectTransactions)),
      switchMap(([_, { amount }]) =>
        // TODO: Remove/Fix first parameter in the moment of integrate de new loan services
        this._customerAPI.requestPreLoan('012345', amount).pipe(
          map((preLoan) => {
            const data: IPutRecord = EVENTS.CREDIT.REQUEST_PRELOAN;
            const extraData: IExtraData = { request: { amount }, response: preLoan };
            this._appFacade.sendPutRecord(data, extraData);
            const event: IPutRecord = EVENTS.CREDIT.EVENT;
            this._appFacade.sendPutRecord(event);
            return customerActions.successRequestPreLoan({ preLoan });
          }),
          catchError((error) => {
            const data: IPutRecord = EVENTS.CREDIT.REQUEST_PRELOAN;
            const extraData: IExtraData = {
              request: { amount },
              response: { error: error?.error ?? error },
            };
            this._appFacade.sendPutRecord(data, extraData);
            return of(customerActions.failureRequestPreLoan({ error }));
          })
        )
      )
    )
  );

  public successRequestPreLoan$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.successRequestPreLoan),
        tap((_) => this._appFacade.setIsLoading()),
        delay(50),
        tap((_) => {
          const isLargeContent: ILargeContentConfig = this._uiService.isLargeContent();
          this._appFacade.setLargeContentConfig(isLargeContent);
        })
      ),
    { dispatch: false }
  );

  public failureRequestPreLoan$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.failureRequestPreLoan),
        delay(50),
        tap((_) => {
          const isLargeContent: ILargeContentConfig = this._uiService.isLargeContent();
          this._appFacade.setLargeContentConfig(isLargeContent);
        })
      ),
    { dispatch: false }
  );

  public createPreLoan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerActions.createPreLoan),
      switchMap(({ createPreLoan }) => {
        this._titleService.setTitle('Transición a pago');
        const event: IPutRecord = EVENTS.CREDIT.EVENT;
        this._appFacade.sendPutRecord(event);
        const transactionId: string = this._handleTokenService.getTransactionId();
        return this._appAPI.getTransactions(transactionId).pipe(
          tap((transactions) => {
            this._appFacade.setTransactions(transactions);
          }),
          switchMap((transactions) => {
            const preLoanId = transactions.others?.pre_loan_id;
            return !preLoanId
              ? this._customerAPI.createPreLoans(createPreLoan).pipe(
                  map((preLoan: IPreLoan) => {
                    const data: IPutRecord = EVENTS.CREDIT.REQUEST_PRELOAN;
                    const extraData: IExtraData = { request: createPreLoan, response: preLoan };
                    this._appFacade.sendPutRecord(data, extraData);
                    return customerActions.successCreatePreLoan(preLoan);
                  }),
                  catchError((error) => of(customerActions.failureCreatePreLoan({ error })))
                )
              : of(customerActions.successCreatePreLoan({ id: preLoanId }));
          })
        );
      })
    )
  );

  public successCreatePreLoan$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.successCreatePreLoan),
        withLatestFrom(
          this._store.select(customerSelectors.selectCustomerValidation),
          this._store.select(customerSelectors.selectCustomerCard)
        ),
        tap(([_, has_card, cardInfo]) => {
          const deviceData: string = this._paymentFacade.getDeviceData();
          const paymentParams: IPaymentParams = {
            device_session_id: deviceData,
          };

          const hasLoanWithCard = !has_card || !cardInfo;

          if (hasLoanWithCard) this._handlePaymentIfNotHasCard();

          const isLoadingPreLoan = false;

          const route: string = ROUTES.PAYMENT_STATUS;
          if (!hasLoanWithCard) {
            this._utilService.navigateTo(route);
          }

          return hasLoanWithCard
            ? this._customerFacade.updateIsLoadingPreLoan(isLoadingPreLoan)
            : this._paymentFacade.createPaymentsIfHasCard(paymentParams);
        })
      ),
    { dispatch: false }
  );

  private _setISODate(): string {
    return this._utilService.getISODate();
  }

  private _handlePaymentIfNotHasCard = () => {
    const route: string = ROUTES.PAYMENT_METHOD;
    this._utilService.navigateTo(route);
    this._appFacade.nextStep();
  };

  public failureCreatePreLoan$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.failureCreatePreLoan),
        tap(({ error }) => {
          const message = this._mapErrorService.mapError(error);
          this._errorFacade.handleError(message);
          const route: string = ROUTES.ERROR;
          this._utilService.navigateTo(route);
        })
      ),
    { dispatch: false }
  );

  public redirectToEcommerce$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.redirectToEcommerce),
        withLatestFrom(this._store.select(customerSelectors.selectRedirectUrl)),
        tap(([{ portal }, redirectUrl]) => {
          return portal
            ? this._redirectToPortal(redirectUrl)
            : this._redirectToEcommerce(redirectUrl);
        })
      ),
    { dispatch: false }
  );

  private _redirectToPortal = (redirectUrl: string) => {
    const portalRoute: string = ENV.portal_url;
    this._closeSession();
    this._utilService.onOpenLink(portalRoute);
    this._utilService.redirectToUrl(redirectUrl);
  };

  private _redirectToEcommerce = (redirectUrl: string) => {
    this._closeSession();
    this._utilService.redirectToUrl(redirectUrl);
  };

  private _closeSession = () => {
    this._handleTokenService.removeAccessToken();
    this._handleTokenService.cancelTransactionId();
    this._handleTokenService.removeUserStep();
    this._handleTokenService.removeTransactionMinMax();
  };

  public getCustomerCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerActions.getCustomerCard),
      switchMap(() =>
        this._customerAPI.getCustomerCard().pipe(
          map((cardInfo?: ICustomerCard) => {
            return customerActions.successGetCustomerCard({ cardInfo });
          }),
          catchError((error) => of(customerActions.failureGetCustomerCard({ error })))
        )
      )
    )
  );

  public successGetCustomerCard$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.successGetCustomerCard),
        withLatestFrom(this._store.select(appSelectors.selectPurchaseLessOnboardingFlag)),
        tap(([{ cardInfo }, isPurchaseLessOnboarding]) => {
          if (!isPurchaseLessOnboarding) {
            const deviceSessionId: string = this._paymentFacade.getDeviceData();
            return this._paymentFacade.setDeviceSessionId(deviceSessionId);
          }
          return this._customerFacade.validateCustomerCard(cardInfo);
        })
      ),
    { dispatch: false }
  );

  public validateCustomerCard$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.validateCustomerCard),
        tap(({ cardInfo }) => {
          if (!cardInfo || cardInfo.masked_number === null) {
            this._appFacade.setNewStep(EONBOARDING_STEPS.PAYMENT_METHOD);
            this._utilService.navigateTo(ROUTES.PAYMENT_METHOD);
            return;
          }
          this._customerFacade.requestCustomerScore();
        })
      ),
    { dispatch: false }
  );

  public requestCustomerScore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customerActions.requestCustomerScore),
      switchMap((_) => {
        return this._customerAPI.requestCustomerScore().pipe(
          map((score) => customerActions.successRequestCustomerScore({ score })),
          catchError((error) => of(customerActions.failureRequestCustomerScore({ error })))
        );
      })
    )
  );

  public successRequestCustomerScore$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.successRequestCustomerScore),
        withLatestFrom(
          this._store.select(appSelectors.selectPurchaseLessOnboardingFlag),
          this._store.select(customerSelectors.selectCustomerCard)
        ),
        tap(([_, isPurchaselessOnboarding, cardInfo]) => {
          if (!!isPurchaselessOnboarding && !!cardInfo) {
            this._utilService.navigateTo(ROUTES.COMPLETED_PROFILE);
            return this._paymentFacade.setLoading(false);
          }
        })
      ),
    { dispatch: false }
  );

  public failureRequestCustomerScore$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(customerActions.failureRequestCustomerScore),
        withLatestFrom(this._store.select(appSelectors.selectSession)),
        tap(([_, session]) => {
          const errorContent = ERROR[EERROR_PAGE_TYPES.ERROR_01];
          this._appFacade.killPurchaseWithoutRedirect(
            'error-request-customer-score',
            session?.is_customer,
            errorContent
          );
        })
      ),
    { dispatch: false }
  );
}
